// Coderblock social-login helper for Supabase apps.
// Import as: `import { coderblockAuth } from "@/integrations/coderblock/auth";`
//
// Supabase-runtime apps have no custom backend, and a single shared GitHub
// OAuth app can only register ONE callback URL — so we cannot enable GoTrue's
// per-project external providers. Instead (exactly like Lovable's cloud-auth)
// Coderblock runs a shared OAuth relay: it verifies the Google/GitHub identity
// with Coderblock's own OAuth apps, then admin-mints a real Supabase session
// for this project and hands the tokens back in the URL fragment. We just call
// `signInWithOAuth(provider)` to start, and `completeSocialLogin()` on the
// return page to finish (`supabase.auth.setSession`).
import { supabase } from "@/integrations/supabase/client";

const OAUTH_BASE =
  (import.meta.env.VITE_CODERBLOCK_OAUTH_BASE as string) ||
  "https://api.coderblock.ai/api/v1/oauth";

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string) || "";

// postMessage type used to hand the OAuth result from the popup back to the
// app-editor preview iframe that opened it.
const SOCIAL_LOGIN_MESSAGE = "coderblock:social-login";

export type SocialProvider = "google" | "github";

export interface SocialLoginResult {
  ok: boolean;
  provider?: string;
  error?: string;
}

export interface SignInOptions {
  /**
   * Called when the embedded-preview flow settles: `{ ok: true }` on success,
   * or `{ ok: false, error }` on failure (`popup_blocked`, `timeout`, a relay
   * error code, etc.). Ignored for the top-level redirect flow (the page
   * navigates away there).
   */
  onComplete?: (result: SocialLoginResult) => void;
}

// Wire-shape of the popup→opener handoff message. Carries the raw tokens so the
// OPENER (the editor iframe) can apply the session in ITS OWN storage context —
// see the storage-partitioning note on `signInWithOAuth`.
interface SocialLoginMessage extends SocialLoginResult {
  type: typeof SOCIAL_LOGIN_MESSAGE;
  access_token?: string;
  refresh_token?: string;
}

interface SocialReturn {
  error?: string;
  provider?: string;
  accessToken?: string;
  refreshToken?: string;
  /** Relay flow: this window is a throwaway popup/tab and should close itself. */
  close?: boolean;
}

/**
 * Are we running inside the Coderblock app-editor preview iframe (as opposed to
 * the direct `preview-*.coderblock.dev` link or production `*.coderblock.app`)?
 */
function isEmbeddedPreview(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin parent → accessing window.top throws → we ARE framed.
    return true;
  }
}

function buildStartUrl(provider: SocialProvider, relay?: string): string {
  const params = new URLSearchParams({
    return_url: window.location.origin,
    supabase_url: SUPABASE_URL,
  });
  if (relay) params.set("relay", relay);
  return `${OAUTH_BASE}/start/${provider}?${params.toString()}`;
}

/**
 * One-time, high-entropy nonce used to bind a sign-in attempt to the relay slot
 * the backend stashes the session under (see the relay-polling note below).
 */
function makeRelayNonce(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID().replace(/-/g, "");
    }
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
      const a = new Uint8Array(16);
      crypto.getRandomValues(a);
      return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch {
    /* fall through to the Math.random fallback */
  }
  return (
    Math.random().toString(36).slice(2) +
    Date.now().toString(36) +
    Math.random().toString(36).slice(2)
  );
}

/**
 * Open the OAuth window, **always preserving `window.opener`**.
 *
 * We must NEVER pass `noopener`: the returned window has to keep a handle back
 * to us so it can `postMessage` the session tokens (the whole hand-off below
 * depends on it).
 *
 * Two-step open:
 *   1. A sized, windowed popup (best UX) — if the user allows popups, this is a
 *      real popup window.
 *   2. Plain `_blank` — popup blockers that reject *windowed* popups (a named
 *      window + feature string) still allow a `_blank` open (a tab). This is the
 *      reliable opener; without `noopener` the hand-off still works.
 *
 * Returns null ONLY if popups are fully blocked. The caller must then NOT
 * navigate the iframe (a framed full-page redirect to the relay just yields a
 * blank "refused to connect").
 */
function openOAuthPopup(url: string): Window | null {
  const w = 480;
  const h = 640;
  const left = Math.max(0, window.screenX + (window.outerWidth - w) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - h) / 2);
  const features =
    `popup=1,width=${w},height=${h},left=${left},top=${top},` +
    `menubar=no,toolbar=no,location=no,status=no,scrollbars=yes,resizable=yes`;
  return window.open(url, "coderblock-oauth", features) || window.open(url, "_blank");
}

/**
 * Sign in with a social provider via Coderblock's OAuth relay.
 *
 * - **Top-level window** (direct preview link / production): full-page redirect
 *   to the relay, which returns to `${origin}/auth/social/complete` with the
 *   session in the URL fragment. Simplest, works everywhere.
 * - **Inside the app-editor iframe**: a full-page redirect would point the frame
 *   at accounts.google.com / the relay, which both refuse to be framed
 *   (`X-Frame-Options`). So we run the flow in a popup.
 *
 *   IMPORTANT — why a backend relay (not postMessage): the editor iframe is a
 *   *third-party* context, so (a) Google sends `Cross-Origin-Opener-Policy:
 *   same-origin`, which SEVERS `window.opener` — the popup can't `postMessage`
 *   back; and (b) storage partitioning isolates the popup's first-party storage
 *   from the iframe's. So neither browser channel can carry the session.
 *
 *   Instead we mint a one-time `relay` nonce, pass it through the flow, and the
 *   backend stashes the minted Supabase session under it. We then POLL
 *   `GET /oauth/session/{nonce}` and, once it returns the tokens, call
 *   `supabase.auth.setSession` HERE (in the iframe's own context) — that's what
 *   actually logs the embedded app in. (`postMessage` is kept only as a fast
 *   path for the rare browser where `opener` survives.)
 */
export function signInWithOAuth(provider: SocialProvider, options?: SignInOptions): void {
  if (!isEmbeddedPreview()) {
    window.location.href = buildStartUrl(provider);
    return;
  }

  const LOG = "[coderblock-auth]";
  const relayNonce = makeRelayNonce();

  // Open the OAuth window. CRITICAL: inside the editor iframe `window.open`
  // frequently returns `null` (cross-origin / COOP / opens as a new tab) EVEN
  // WHEN the window actually opens fine. So we must NOT bail when the handle is
  // null — the relay-polling below doesn't need it (the session travels through
  // the backend, keyed by our nonce). The handle is only an optional fast-path
  // (postMessage hand-off + auto-close on success).
  const popup = openOAuthPopup(buildStartUrl(provider, relayNonce));
  if (!popup) {
    console.warn(`${LOG} no window handle (new tab / COOP) — polling relay anyway`);
  }

  let settled = false;
  let pollTimer = 0;
  const cleanup = () => {
    window.removeEventListener("message", onMessage);
    document.removeEventListener("visibilitychange", onWake);
    window.removeEventListener("focus", onWake);
    window.clearTimeout(pollTimer);
  };
  const finish = (result: SocialLoginResult) => {
    if (settled) return;
    settled = true;
    cleanup();
    if (result.ok) {
      console.info(`${LOG} signed in (${result.provider || provider})`);
      try { popup?.close(); } catch { /* no handle / opener severed by COOP — harmless */ }
    } else {
      console.warn(`${LOG} sign-in did not complete:`, result.error);
    }
    options?.onComplete?.(result);
  };

  const applyTokens = async (
    accessToken?: string,
    refreshToken?: string,
    prov?: string,
  ): Promise<boolean> => {
    if (!accessToken || !refreshToken) return false;
    try {
      const { error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      finish(
        error
          ? { ok: false, provider: prov, error: error.message }
          : { ok: true, provider: prov },
      );
    } catch {
      finish({ ok: false, provider: prov, error: "session_apply_failed" });
    }
    return true;
  };

  // Fast path: postMessage from the popup. Usually severed by COOP in the editor
  // iframe, but free to keep for the odd browser where `opener` survives.
  const onMessage = async (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    const data = event.data as Partial<SocialLoginMessage>;
    if (!data || data.type !== SOCIAL_LOGIN_MESSAGE) return;
    if (data.ok && (await applyTokens(data.access_token, data.refresh_token, data.provider))) {
      return;
    }
    finish({ ok: !!data.ok, provider: data.provider, error: data.error });
  };
  window.addEventListener("message", onMessage);

  // Robust path: poll the relay for the minted session. Survives COOP + storage
  // partitioning because the session travels server-side, keyed by our nonce.
  const POLL_INTERVAL_MS = 1500;
  const deadline = Date.now() + 3 * 60 * 1000; // 3 min: consent + first poll

  // One relay check. Returns true once the attempt is settled (caller stops).
  const checkRelayOnce = async (): Promise<boolean> => {
    if (settled) return true;
    try {
      const res = await fetch(`${OAUTH_BASE}/session/${encodeURIComponent(relayNonce)}`, {
        method: "GET",
        cache: "no-store",
      });
      if (!res.ok) {
        console.warn(`${LOG} relay poll HTTP ${res.status}`);
        return settled;
      }
      const data = (await res.json()) as {
        status?: string;
        access_token?: string;
        refresh_token?: string;
        provider?: string;
        error?: string;
      };
      if (data?.status === "ready") {
        console.info(`${LOG} relay session ready — applying`);
        if (data.error) {
          finish({ ok: false, provider: data.provider || provider, error: data.error });
          return true;
        }
        if (await applyTokens(data.access_token, data.refresh_token, data.provider || provider)) {
          return true;
        }
      }
    } catch (e) {
      // A throw here (vs an HTTP error above) means the request never reached the
      // server — almost always a CSP `connect-src` block or a network failure.
      console.warn(`${LOG} relay poll request failed (CSP/network?)`, e);
    }
    return settled;
  };

  const poll = async () => {
    if (settled) return;
    await checkRelayOnce();
    if (settled) return;
    if (Date.now() > deadline) {
      finish({ ok: false, provider, error: "timeout" });
      return;
    }
    pollTimer = window.setTimeout(poll, POLL_INTERVAL_MS);
  };

  // Background tabs throttle/freeze setTimeout, so while the user is in the OAuth
  // popup our interval may never fire. Re-check the instant the editor tab/iframe
  // regains visibility or focus — that's exactly when the session is ready.
  const onWake = () => {
    if (settled) return;
    if (document.visibilityState === "visible") void checkRelayOnce();
  };

  console.info(`${LOG} awaiting OAuth (relay ${relayNonce.slice(0, 6)}…)`);
  // Immediate probe: guarantees one GET /oauth/session/<nonce> the instant
  // sign-in starts. If even this never reaches the backend, the fetch itself is
  // blocked (CSP/network), not the timer/visibility logic.
  void checkRelayOnce();
  pollTimer = window.setTimeout(poll, POLL_INTERVAL_MS);
  document.addEventListener("visibilitychange", onWake);
  window.addEventListener("focus", onWake);

  // NB: we deliberately do NOT watch `popup.closed`. Once Google sends its
  // `Cross-Origin-Opener-Policy` header the popup leaves our browsing-context
  // group, so our handle reports `closed === true` even while it's still open —
  // watching it would fire a bogus "cancelled" mid-login. The poll deadline
  // above is the single source of truth for giving up.
}

/**
 * Call this on the `/auth/social/complete` route. Reads the session tokens from
 * the URL fragment (or an `?error=`).
 *
 * - **Popup/tab opened by the embedded-preview flow** (`window.opener` set):
 *   hand the tokens to the opener (same origin) so IT applies the session in
 *   its own partition, then close. We do NOT rely on shared storage.
 * - **Standalone window** (direct link / production full-page redirect): apply
 *   the session locally as usual.
 */
export async function completeSocialLogin(): Promise<SocialLoginResult> {
  const ret = readSocialReturn();

  if (window.opener && window.opener !== window) {
    const message: SocialLoginMessage = {
      type: SOCIAL_LOGIN_MESSAGE,
      ok: !ret.error && !!ret.accessToken && !!ret.refreshToken,
      provider: ret.provider,
      error: ret.error,
      access_token: ret.accessToken,
      refresh_token: ret.refreshToken,
    };
    try {
      window.opener.postMessage(message, window.location.origin);
    } catch {
      /* opener gone / cross-origin — nothing we can do */
    }
    cleanUrl();
    // The opener owns the real session; closing returns focus to the editor.
    try {
      window.close();
    } catch {
      /* close blocked — harmless, the opener already has the tokens */
    }
    return { ok: message.ok, provider: ret.provider, error: ret.error };
  }

  const result = await applySessionLocally(ret);

  // Relay/embedded-preview flow: the editor iframe already picked up the session
  // via polling, so this popup/tab has done its job — close it. (The top-level
  // direct-link flow has no `close` marker, so it stays put.) A script-opened
  // window may close itself even though COOP severed `window.opener`.
  if (ret.close) {
    try {
      window.close();
    } catch {
      /* close blocked — harmless; the editor iframe is already signed in */
    }
  }

  return result;
}

function readSocialReturn(): SocialReturn {
  // Errors come back as a normal query string (?error=...&provider=...).
  const search = new URLSearchParams(window.location.search);
  const err = search.get("error");
  if (err) {
    return { error: err, provider: search.get("provider") || undefined };
  }
  // Success: tokens are in the fragment (#access_token=...&refresh_token=...).
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  return {
    provider: hash.get("provider") || undefined,
    accessToken: hash.get("access_token") || undefined,
    refreshToken: hash.get("refresh_token") || undefined,
    close: hash.get("close") === "1",
  };
}

function cleanUrl(): void {
  window.history.replaceState(null, "", window.location.pathname);
}

async function applySessionLocally(ret: SocialReturn): Promise<SocialLoginResult> {
  if (ret.error) {
    cleanUrl();
    return { ok: false, error: ret.error, provider: ret.provider };
  }

  if (ret.accessToken && ret.refreshToken) {
    const { error } = await supabase.auth.setSession({
      access_token: ret.accessToken,
      refresh_token: ret.refreshToken,
    });
    cleanUrl();
    return error
      ? { ok: false, error: error.message, provider: ret.provider }
      : { ok: true, provider: ret.provider };
  }

  // supabase-js `detectSessionInUrl` (on by default) may have already consumed
  // the fragment and applied the session before this ran — treat an existing
  // session as success rather than reporting "missing_tokens".
  const { data } = await supabase.auth.getSession();
  cleanUrl();
  return data.session ? { ok: true, provider: ret.provider } : { ok: false, error: "missing_tokens" };
}

export const coderblockAuth = { signInWithOAuth, completeSocialLogin };
export default coderblockAuth;
